import { type FC, type RefObject } from 'react';
import type { VirtualItem } from '@tanstack/react-virtual';
import type { DataTableColumnGroupingData, DataTableDimensions } from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableGroupHeader } from './DataTableGroupHeader';

interface ScrollableGroupHeadersProps {
  columnGroupingData: DataTableColumnGroupingData;
  virtualColumns: Array<VirtualItem>;
  stickyColumnCount: number;
  dimensions: DataTableDimensions;
  scrollElementRef: RefObject<HTMLElement>;
  pinnedColumnWidth: number;
}

export const ScrollableGroupHeaders: FC<ScrollableGroupHeadersProps> = ({
  columnGroupingData,
  virtualColumns,
  stickyColumnCount,
  dimensions,
  scrollElementRef,
  pinnedColumnWidth,
}) => {
  const renderedGroups = new Set<string>();

  return (
    <>
      {virtualColumns.map((virtualColumn) => {
        const leafIndex = stickyColumnCount + virtualColumn.index;
        const group = columnGroupingData.columnGroupMap.get(leafIndex);

        if (!group || renderedGroups.has(group.parentColumn.id)) {
          return null;
        }

        const groupEnd = group.startIndex + group.colspan;
        if (groupEnd <= stickyColumnCount) {
          return null;
        }

        renderedGroups.add(group.parentColumn.id);

        const visibleStart = Math.max(group.startIndex, stickyColumnCount);
        const visibleEnd = groupEnd;
        const visibleColspan = visibleEnd - visibleStart;

        if (visibleColspan <= 0) {
          return null;
        }

        return (
          <DataTableGroupHeader
            key={group.parentColumn.id}
            group={group}
            visibleColspan={visibleColspan}
            startOffset={(visibleStart - stickyColumnCount) * dimensions.cellWidth}
            dimensions={dimensions}
            scrollElementRef={scrollElementRef}
            pinnedColumnWidth={pinnedColumnWidth}
          />
        );
      })}
    </>
  );
};
